import { ref } from "vue";
import { defineStore } from "pinia";
import { useTenantStore } from "@/stores/tenant";
import AxiosChat from "../config/AxiosChatConfig";
import { chatroomStatus } from "@/stores/messagesStore";
import { socketConn } from "./socketConn";
import { groupMembers } from "../components/settings/inbox/inbox-component/modals/newMessage/globalStates";
import { chatroomStore } from "./messagesStore";
import { useEmitter } from "@/composables/useEmitter";

export const useChatStore = defineStore("chatStore", () => {
    const user_detail = ref({});
    const toggle_chat = ref(false);
    const selection = ref(false);
    const chat_list = ref([]);
    const agentDetail = ref({
        id: null,
    });
    const loadingState = ref({
        startChat: false,
        contact_list: false,
    });
    const allGroups = ref([]);

    // [info]: composables
    const { getTenant } = useTenantStore();
    const emitter = useEmitter();

    // [info]: methods
    const getCommonGroups = (user) => {
        let commonGroups = [];
        chat_list.value?.filter((chat) => {
            if (chat?.chatName !== "1:1") {
                chat.users.some((item) => item._id === user) ? commonGroups.push(chat) : null;
            }
        });
        return commonGroups;
    };

    const getMemberDetailFromNode = async (contact) => {
        const tenantId = getTenant.uuid;
        if (!contact || !tenantId) return;

        try {
            const { email, first_name, last_name, uuid, phone } = contact;
            const resp = await AxiosChat.post("user", {
                email,
                firstName: first_name,
                lastName: last_name,
                uuid,
                tenantId: tenantId,
                phone: `${phone}@c.us`,
                role: "Contact",
            });
            const { status, data } = resp;
            if (status === 200 && data) {
                return data;
            }
            return null;
        } catch (e) {
            console.error("[ERR]: at getting member detail from node", e);
        }
    };

    const handleCreateRoom = async (contatId) => {
        const tenantId = getTenant.uuid;
        const agentID = agentDetail.value.id;

        if (!contatId || !tenantId || !agentID) return;

        const resp = await AxiosChat.post("chat", {
            userId: agentID,
            peerId: contatId,
            tenantId,
        });

        if (resp.status === 200) {
            const { users } = resp.data;
            chatroomStore.activeChatContact = resp.data;
            chatroomStore.chatRoom = resp.data;
            emitter.emit("getRoomMessages");

            socketConn.socket.emit("room created", users);
        } else {
            console.error("Data NOT found", resp);
        }
    };

    const handleAccessAgent = async (agentDetails) => {
        if (!getTenant.uuid) return;
        const resp = await AxiosChat.post("user", { ...agentDetails, role: "Agent", tenantId: getTenant.uuid });

        const { status, data } = resp;
        if (status === 200 && data) {
            agentDetail.value.id = resp.data._id;
            return resp.data._id;
        }
        return null;
    };

    const handleCreateGroupChat = async (groupName) => {
        const agentID = agentDetail.value.id;
        const tenantID = getTenant.uuid;
        if (!agentID || !tenantID || !groupName) return;

        try {
            const resp = await AxiosChat.post("chat/group", {
                userId: agentID,
                users: groupMembers.value,
                chatName: groupName,
                tenantId: tenantID,
            });

            if (resp.status === 200) {
                const { users } = resp.data;
                socketConn.socket.emit("room created", users);

                chatroomStore.activeChatContact = resp.data;
                chatroomStore.chatRoom = resp.data;
                emitter.emit("getRoomMessages");
                return true;
            }
        } catch (e) {
            console.error("[ERR]: error at creating a group chat", e);
        }
    };

    const handleFetchAllChats = async (filterCriteria, isSilent) => {
        const tenantId = getTenant.uuid;
        const agentID = agentDetail.value.id;
        const chatStatus = chatroomStatus?.status || "Open";
        allGroups.value = [];

        if (!agentID || !tenantId) return;
        if (!isSilent) loadingState.value.contact_list = true;
        try {
            let resp;
            if (filterCriteria) {
                resp = await AxiosChat.post("chat/filterchats", {
                    userId: agentID,
                    tenantId,
                    chatStatus: chatStatus,
                    labelIDs: filterCriteria?.labelIDs || [],
                    UUIDs: [...(filterCriteria?.contactUUIDs || []), ...(filterCriteria?.agentUUIDs || [])],
                });
            } else {
                resp = await AxiosChat.get(`chat/${agentID}/${tenantId}/${chatStatus}`);
            }
            chat_list.value = resp.data;

            if (resp.status === 200) {
                if (resp.data.length > 0 && socketConn.socket) {
                    resp.data.map((chatRoom) => {
                        if (chatRoom.isGroupChat) {
                            allGroups.value.push(chatRoom);
                        }
                        socketConn.socket.emit("join chat", chatRoom._id);
                    });
                }
            }
        } catch (e) {
            allGroups.value = [];
            console.error("[ERR]: at fetching all chats", e);
        } finally {
            loadingState.value.contact_list = false;
        }
    };

    const removeTargetChat = (id) => {
        chat_list.value = chat_list.value.filter((chat) => chat._id !== id);
    };

    return {
        user_detail,
        toggle_chat,
        selection,
        chat_list,
        getCommonGroups,

        agentDetail,
        allGroups,
        loadingState,
        handleCreateRoom,
        handleAccessAgent,
        handleFetchAllChats,
        getMemberDetailFromNode,
        handleCreateGroupChat,
        removeTargetChat,
    };
});
